// helpers for `service_banner` and `service_inventory` evidence types.
//
// Dorothy ships these as supporting evidence (banner grabs + open-service
// inventories). per spec: NOT vulnerabilities by themselves, neutral
// wording only, no findings.
//
// field extraction follows the SNMP pattern: try PascalCase first
// (Dorothy's preferred encoding), then snake_case, then camelCase, then
// MIB-style fallbacks. returns null for missing fields so renderers can
// hide empty sections

import type { EngagementEvidence } from '@/lib/engagementsApi'
import { normalizeRawJson } from './snmpEvidence'

export const SERVICE_BANNER_TYPE   = 'service_banner'
export const SERVICE_INVENTORY_TYPE = 'service_inventory'

export function isServiceBannerEvidence(ev: { evidence_type?: string | null }): boolean {
  return ev.evidence_type === SERVICE_BANNER_TYPE
}

export function isServiceInventoryEvidence(ev: { evidence_type?: string | null }): boolean {
  return ev.evidence_type === SERVICE_INVENTORY_TYPE
}

// ── service_banner ─────────────────────────────────────────────────────

export interface ServiceBannerFields {
  host_ip:        string | null
  port:           number | null
  protocol:       string | null
  service:        string | null
  service_version: string | null
  banner:         string | null
  http_title:     string | null
  http_server:    string | null
  http_headers:   Record<string, string> | null
  tls_subject:    string | null
  tls_issuer:     string | null
  tls_san:        string[] | null
  tls_not_before: string | null
  tls_not_after:  string | null
  tls_self_signed: boolean | null
  tls_expired:    boolean | null
  tls_version:    string | null
}

function pickString(...candidates: any[]): string | null {
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim()) return c
  }
  return null
}

function pickNumber(...candidates: any[]): number | null {
  for (const c of candidates) {
    if (typeof c === 'number' && isFinite(c)) return c
    if (typeof c === 'string' && c.trim() && !isNaN(Number(c))) return Number(c)
  }
  return null
}

function pickBoolean(...candidates: any[]): boolean | null {
  for (const c of candidates) {
    if (typeof c === 'boolean') return c
  }
  return null
}

function pickArray(...candidates: any[]): any[] | null {
  for (const c of candidates) {
    if (Array.isArray(c)) return c
  }
  return null
}

function pickObject(...candidates: any[]): Record<string, any> | null {
  for (const c of candidates) {
    if (c && typeof c === 'object' && !Array.isArray(c)) return c
  }
  return null
}

export function extractServiceBanner(rawIn: any): ServiceBannerFields {
  const raw = normalizeRawJson(rawIn)
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {
      host_ip: null, port: null, protocol: null, service: null,
      service_version: null, banner: null,
      http_title: null, http_server: null, http_headers: null,
      tls_subject: null, tls_issuer: null, tls_san: null,
      tls_not_before: null, tls_not_after: null,
      tls_self_signed: null, tls_expired: null, tls_version: null,
    }
  }

  // HTTP and TLS payloads may be nested under a sub-object or inline at
  // the top level. resolve a "tls" view that points at whichever shape
  const http = pickObject(raw.http, raw.HTTP, raw.Http) ?? raw
  const tls  = pickObject(raw.tls, raw.TLS, raw.Tls, raw.certificate, raw.cert) ?? raw

  const sans = pickArray(
    tls.tls_san, tls.tls_sans, tls.TLSSAN, tls.TlsSAN,
    tls.san, tls.sans, tls.SubjectAltNames, tls.subject_alt_names, tls.subjectAltNames,
    raw.tls_san, raw.tls_sans, raw.san, raw.sans,
  )

  return {
    host_ip:  pickString(
      raw.host_ip, raw.HostIp, raw.host, raw.Host,
      raw.target_ip, raw.TargetIp, raw.targetIp, raw.target,
      raw.ip, raw.IP, raw.Ip,
    ),
    port: pickNumber(
      raw.port, raw.Port, raw.target_port, raw.TargetPort, raw.targetPort,
      raw.port_number, raw.PortNumber,
    ),
    protocol: pickString(
      raw.protocol, raw.Protocol, raw.proto, raw.transport, raw.Transport,
      raw.service_protocol,
    ),
    service: pickString(
      raw.service, raw.Service, raw.service_name, raw.ServiceName, raw.serviceName,
      raw.app_protocol, raw.AppProtocol,
    ),
    service_version: pickString(
      raw.service_version, raw.ServiceVersion, raw.serviceVersion, raw.version, raw.Version,
      raw.product_version, raw.ProductVersion,
    ),
    banner: pickString(
      raw.banner, raw.Banner, raw.banner_text, raw.BannerText, raw.bannerText,
      raw.response, raw.Response, raw.data, raw.Data,
    ),
    http_title: pickString(
      http.http_title, http.HTTPTitle, http.httpTitle, http.title, http.Title,
      http.page_title, http.PageTitle, http.HtmlTitle, http.html_title,
      raw.http_title, raw.title,
    ),
    http_server: pickString(
      http.http_server, http.HTTPServer, http.httpServer, http.server, http.Server,
      http.server_header, http.ServerHeader,
      raw.http_server, raw.server,
    ),
    http_headers: pickObject(
      http.http_headers, http.HTTPHeaders, http.httpHeaders, http.headers, http.Headers,
      http.response_headers, http.ResponseHeaders,
      raw.http_headers, raw.headers,
    ),
    tls_subject: pickString(
      tls.tls_subject, tls.TLSSubject, tls.tlsSubject, tls.subject, tls.Subject,
      tls.cert_subject, tls.CertSubject,
    ),
    tls_issuer: pickString(
      tls.tls_issuer, tls.TLSIssuer, tls.tlsIssuer, tls.issuer, tls.Issuer,
      tls.cert_issuer, tls.CertIssuer,
    ),
    tls_san: sans ? sans.filter((x): x is string => typeof x === 'string' && !!x.trim()) : null,
    tls_not_before: pickString(
      tls.tls_not_before, tls.TLSNotBefore, tls.tlsNotBefore,
      tls.not_before, tls.NotBefore, tls.notBefore,
      tls.valid_from, tls.ValidFrom, tls.validFrom,
    ),
    tls_not_after: pickString(
      tls.tls_not_after, tls.TLSNotAfter, tls.tlsNotAfter,
      tls.not_after, tls.NotAfter, tls.notAfter,
      tls.valid_to, tls.ValidTo, tls.validTo, tls.expires_at, tls.ExpiresAt,
    ),
    tls_self_signed: pickBoolean(
      tls.tls_self_signed, tls.TLSSelfSigned, tls.tlsSelfSigned,
      tls.self_signed, tls.SelfSigned, tls.selfSigned, tls.isSelfSigned,
    ),
    tls_expired: pickBoolean(
      tls.tls_expired, tls.TLSExpired, tls.tlsExpired,
      tls.expired, tls.Expired, tls.isExpired,
    ),
    tls_version: pickString(
      tls.tls_version, tls.TLSVersion, tls.tlsVersion, tls.version, tls.Version,
    ),
  }
}

// ── service_inventory ──────────────────────────────────────────────────

export interface InventoryServiceRow {
  port:     number | null
  protocol: string | null
  service:  string | null
  version:  string | null
  banner_hint: string | null
}

export interface ServiceInventoryFields {
  host_ip:                 string | null
  services:                InventoryServiceRow[]
  detected_services:       string | null
  device_classification:   string | null
}

export function extractServiceInventory(rawIn: any): ServiceInventoryFields {
  const raw = normalizeRawJson(rawIn)
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { host_ip: null, services: [], detected_services: null, device_classification: null }
  }

  const arr = pickArray(
    raw.open_services, raw.OpenServices, raw.openServices,
    raw.services, raw.Services,
    raw.open_ports, raw.OpenPorts, raw.openPorts,
  ) ?? []

  const services: InventoryServiceRow[] = arr.map((r: any) => ({
    port:     pickNumber(r.port, r.Port, r.port_number, r.PortNumber),
    protocol: pickString(r.protocol, r.Protocol, r.proto, r.transport),
    service:  pickString(r.service, r.Service, r.service_name, r.ServiceName, r.app_protocol),
    version:  pickString(r.version, r.Version, r.service_version, r.ServiceVersion),
    banner_hint: pickString(r.banner_hint, r.BannerHint, r.banner, r.Banner, r.hint, r.product),
  }))

  return {
    host_ip: pickString(
      raw.host_ip, raw.HostIp, raw.host, raw.Host,
      raw.target_ip, raw.TargetIp, raw.targetIp, raw.target,
      raw.ip, raw.IP,
    ),
    services,
    detected_services: pickString(
      raw.detected_services, raw.DetectedServices, raw.detectedServices,
      raw.summary, raw.Summary,
    ),
    device_classification: pickString(
      raw.device_classification, raw.DeviceClassification, raw.deviceClassification,
      raw.device_type_hint, raw.DeviceTypeHint, raw.deviceTypeHint,
      raw.device_hint, raw.DeviceHint, raw.device_type, raw.DeviceType,
    ),
  }
}

// ── Summary strings for the Evidence table cell ────────────────────────

// service_banner: "192.168.0.24:80 HTTP · title collected"
// service_inventory: "8 open service(s) identified"
export function serviceEvidenceKeySummary(ev: EngagementEvidence): string | null {
  if (ev.evidence_type === SERVICE_BANNER_TYPE) {
    const f = extractServiceBanner(ev.raw_json)
    const endpoint = (f.host_ip && f.port) ? `${f.host_ip}:${f.port}`
                   : f.host_ip ? f.host_ip
                   : f.port ? `:${f.port}` : null

    // choose evidence noun by what was actually collected
    const noun = f.tls_subject || f.tls_issuer || f.tls_not_after
      ? 'certificate collected'
      : f.http_title || f.http_server
      ? 'title/header collected'
      : f.banner
      ? 'banner collected'
      : f.service
      ? 'service identified'
      : 'service evidence collected'

    const proto = f.service ?? f.protocol ?? null
    const parts: string[] = []
    if (endpoint) parts.push(endpoint)
    if (proto)    parts.push(proto)
    return [parts.join(' '), noun].filter(Boolean).join(' · ')
  }

  if (ev.evidence_type === SERVICE_INVENTORY_TYPE) {
    const f = extractServiceInventory(ev.raw_json)
    const n = f.services.length
    if (n === 0 && f.detected_services) return f.detected_services
    if (n === 0) return ev.summary ?? 'Open service inventory'
    return `${n} open service${n === 1 ? '' : 's'} identified by banner grab`
  }

  return null
}
